import DataHash from '../classes/data-hash';

/**
 * Class representing the Housing Activity Geography Selection.
 */
export default class HousingAffordabilityGeoSelect {
    DataPageInstance;

    /**
     * Location data received from the server.
     * @type {Object}
     */
    geoData;

    /**
     * button to clear Geo selectors
     */
    resetBtn;

    stateSelect = {
        name: 'State',
        selectorId: 'area_type_1_select',
        geoTypeId: 1,
        choice: {},
        hashKey: 'State',
    };

    dynamicSelects = [
        {
            name: 'Metropolitan Statistical Area',
            selectorId: 'area_type_2_select',
            hashKey: 'MSA',
            geoTypeId: 2,
            choice: {},
        },
        {
            name: 'Metropolitan Division',
            selectorId: 'area_type_3_select',
            hashKey: 'MD',
            geoTypeId: 3,
            choice: {},
        },
        {
            name: 'County',
            selectorId: 'area_type_4_select',
            hashKey: 'County',
            geoTypeId: 4,
            choice: {},
        },
    ];

    /**
     * Create a HousingAffordabilityGeoSelect instance.
     * @param {Object} DataPageInstance - The instance of the Data Page.
     * @param {Object} geoData - Location data received from the server.
     */
    constructor(DataPageInstance, geoData) {
        this.DataPageInstance = DataPageInstance;

        //
        this.geoData = geoData;

        // Initialize selects
        this.initializeStaticSelects();
        this.initializeDynamicSelects();

        //
        this.initializeResetButton();

        //
        this.HashInstance = new DataHash('#!/activity/', 'key_value');
        this.initializeQueryParameters();
        //
        this.setInitialFilters();
    }

    /**
     * Initialize static select elements.
     */
    initializeStaticSelects() {
        const state = this.stateSelect;
        const stateGeoData = this.geoData[state.selectorId];
        const stateSelectOrder =
            this.geoData['area_type_1_select_order'] ??
            Object.keys(stateGeoData);

        state['choice'] = new Choices(`#${state.selectorId}`, {
            position: 'bottom',
            itemSelectText: '',
            allowHTML: true,
            shouldSort: false,
            classNames: {
                itemDisabled: 'hidden',
            },
        });

        for (const key in stateSelectOrder) {
            const stateIndex = stateSelectOrder[key];
            state['choice'].setChoices([
                {
                    value: stateIndex,
                    label: stateGeoData[stateIndex],
                },
            ]);
        }

        // Event Handler For Select
        state['choice'].passedElement.element.addEventListener(
            'change',
            this.stateChoiceEventHandler.bind(this),
        );
    }

    /**
     * Initialize static select elements.
     */
    initializeDynamicSelects() {
        this.dynamicSelects.forEach((el) => {
            el['choice'] = new Choices(`#${el.selectorId}`, {
                position: 'bottom',
                itemSelectText: '',
                allowHTML: true,
                placeholderValue: el.name,
            });

            // Event Handler For Select
            el['choice'].passedElement.element.addEventListener(
                'change',
                () => {
                    this.dynamicChoiceEventHandler(el);
                },
            );
        });
    }

    initializeResetButton() {
        const btn = document.querySelector('.js-geo-select-reset');
        if (!btn) {
            return;
        }
        this.resetBtn = btn;
        this.enableResetBtn();

        this.resetBtn.addEventListener(
            'click',
            this.resetBtnHandler.bind(this),
        );
    }

    /**
     * Event handler for the state select element.
     */
    stateChoiceEventHandler() {
        this.resetDynamicSelectorChoices();
        this.updateDynamicSelectsOptions();
        this.updateDynamicSelectsAvailability();
        this.DataPageInstance.gridRefresh();
    }

    /**
     * Update dynamic select elements with options based on the active state.
     */
    updateDynamicSelectsOptions() {
        const state = this.getActiveState();

        this.dynamicSelects.forEach((el) => {
            const elArray = this.geoData[el.selectorId][state];

            if (!elArray) {
                return;
            }

            for (const key in elArray) {
                el['choice'].setChoices([
                    {
                        value: key,
                        label: elArray[key],
                    },
                ]);
            }
        });
    }

    /**
     * Update the visibility and availability of dynamic select elements.
     */
    updateDynamicSelectsAvailability() {
        this.dynamicSelects.forEach((el) => {
            const options =
                el.choice.choiceList.element.querySelectorAll('.choices__item');
            if (options.length > 1) {
                el.choice.enable();
                return;
            }
            el.choice.disable();
        });
    }

    /**
     * Event handler for the state select element.
     */
    dynamicChoiceEventHandler(el) {
        const type = el.name;
        this.dynamicSelects.forEach((el) => {
            if (type === el.name) {
                return;
            }
            el.choice.setChoiceByValue('');
        });

        // Clear the State selection in order to be able to click it again.
        const stateChoice = this.stateSelect.choice;
        stateChoice.setChoiceByValue('');

        this.DataPageInstance.gridRefresh();
    }

    resetBtnHandler(event) {
        event.preventDefault();

        if (!this.resetBtn.classList.contains('active')) {
            return;
        }

        this.resetButtonEventStart();

        this.stateSelect.choice.setChoiceByValue('');
        this.setInitialFilters(true);

        this.resetBtnAvailability(false);

        this.resetButtonEventEnd();
    }

    /**
     * Initialize hash data based on the URL hash.
     */
    initializeQueryParameters() {
        const hashArray = this.HashInstance.getHash();
        const hashType = hashArray[0];
        const hashName = decodeURIComponent(hashArray[1]);

        if (!hashType || !hashName) {
            return;
        }

        if (hashType === 'State') {
            const stateData = this.geoData[this.stateSelect.selectorId];

            for (const stateId in stateData) {
                if (hashName !== stateData[stateId]) {
                    continue;
                }
                this.stateSelect['choice'].setChoiceByValue(stateId);
                // Trigger the 'change' event forcefully to allow other handlers to proceed.
                this.stateSelect.choice.passedElement.element.dispatchEvent(
                    new Event('change'),
                );
                break;
            }
            return;
        }

        this.dynamicSelects.forEach((el) => {
            if (hashType !== el.hashKey) {
                return;
            }
            const data = this.geoData[el.selectorId];

            for (const key in data) {
                const dataArray = data[key];

                if (typeof dataArray !== 'object') {
                    continue;
                }

                for (const placeId in dataArray) {
                    const placeName = dataArray[placeId];

                    // geo names might be not equal, like 'Amarilio' & 'Amarilio, TX'
                    if (
                        !hashName.includes(placeName) &&
                        !placeName.includes(hashName)
                    ) {
                        continue;
                    }

                    this.stateSelect['choice'].setChoiceByValue(key);
                    // Trigger the 'change' event forcefully to allow other handlers to proceed.
                    this.stateSelect.choice.passedElement.element.dispatchEvent(
                        new Event('change'),
                    );

                    //
                    el['choice'].setChoiceByValue(placeId);
                    // Trigger the 'change' event forcefully to allow other handlers to proceed.
                    el['choice'].passedElement.element.dispatchEvent(
                        new Event('change'),
                    );
                    break;
                }
            }
        });
    }

    /**
     * Reset the choices in dynamic select elements.
     */
    resetDynamicSelectorChoices() {
        this.dynamicSelects.forEach((el) => {
            el.choice.destroy();
            el.choice.init();
        });
    }

    /**
     * Set the URL query parameters based on the selected options.
     */
    setQueryParameters() {
        const requestParams = this.getRequestParams();
        if (requestParams.hashKey && requestParams.geoName) {
            this.HashInstance.setHash([
                requestParams.hashKey,
                requestParams.geoName,
            ]);
            return;
        }
        this.HashInstance.removeHash();
    }

    /**
     * Get the request parameters based on the selected options.
     * @returns {Object} - The request parameters.
     */
    getRequestParams() {
        let params = {
            geoId: '',
            geoName: '',
            geoTypeId: '',
            geoType: '',
            stateId: '',
            stateName: '',
            hashKey: '',
        };
        //
        this.dynamicSelects.forEach((el) => {
            const selectorValue = el.choice.getValue();
            if (selectorValue.value) {
                params = {
                    geoId: selectorValue.value,
                    geoName: selectorValue.label,
                    geoTypeId: el.geoTypeId,
                    geoType: el.name,
                    stateId: this.stateSelect.choice.getValue(true),
                    stateName: this.stateSelect.choice.getValue().label,
                    hashKey: el.hashKey,
                };
            }
        });
        if (params.geoId) {
            return params;
        }

        //
        const stateId = this.getActiveState();
        if (stateId) {
            params = {
                geoId: stateId,
                geoName: this.geoData[this.stateSelect.selectorId][stateId],
                geoTypeId: this.stateSelect.geoTypeId,
                geoType: 'State',
                stateId: this.stateSelect.choice.getValue(true),
                stateName: this.stateSelect.choice.getValue().label,
                hashKey: this.stateSelect.hashKey,
            };
        }
        if (params.geoId) {
            return params;
        }
        //
        return params;
    }

    /**
     * Get the active state from the state select element.
     * @returns {string} - The active state name.
     */
    getActiveState() {
        return this.stateSelect.choice.getValue(true);
    }

    resetBtnAvailability(visible) {
        const el = this.resetBtn;
        //
        if (visible) {
            el.classList.remove('!hidden');
        } else {
            el.classList.add('!hidden');
        }
    }

    /**
     * Fill children selectors with Texas by default.
     * @param forceSet {boolean}
     */
    setInitialFilters(forceSet = false) {
        const hashArray = this.HashInstance.getHash();
        const hashType = hashArray[0];
        const hashName = decodeURIComponent(hashArray[1]);

        // do not change state selector if anything has been set.
        if (hashType && hashName && !forceSet) {
            return;
        }

        const stateChoice = this.stateSelect.choice;
        stateChoice.setChoiceByValue('26');
        //
        this.resetDynamicSelectorChoices();
        this.updateDynamicSelectsOptions();
        this.updateDynamicSelectsAvailability();
        //
        stateChoice.setChoiceByValue('');
    }

    /**
     * Disable all select elements.
     */
    disableSelects() {
        this.stateSelect.choice.disable();
        this.dynamicSelects.forEach((el) => {
            el.choice.disable();
        });
        this.disableResetBtn();
    }

    /**
     * Enable all select elements.
     */
    enableSelects() {
        this.stateSelect.choice.enable();
        this.updateDynamicSelectsAvailability();
        this.enableResetBtn();
    }

    enableResetBtn() {
        const el = this.resetBtn;
        //
        el.classList.add('active');
        el.classList.remove('pointer-events-none');
    }

    disableResetBtn() {
        const el = this.resetBtn;
        el.classList.remove('active');
        el.classList.add('pointer-events-none');
    }

    resetButtonEventStart() {
        const event = new CustomEvent('resetData.Start');
        document.dispatchEvent(event);
    }

    resetButtonEventEnd() {
        const event = new CustomEvent('resetData.End');
        document.dispatchEvent(event);
    }
}
